"use client";

import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { CountryCodeSelect } from "@/components/sections/contact/CountryCodeSelect";
import { CurrencySelect } from "@/components/sections/contact/CurrencySelect";
import { PillButton } from "@/components/site/PillButton";
import { useContent } from "@/components/site/ContentProvider";
import {
  ContactSubmissionError,
  submitContactForm,
} from "@/lib/contact-api";
import {
  contactFormErrorsFromZod,
  contactFormSchema,
  currencyForCountry,
  defaultPhoneCountryIso,
  detectPhoneCountryIso,
  formatBudgetWithCaret,
  type ContactCurrency,
  type ContactFormErrors,
  type ContactFormValues,
  type PhoneCountryIso,
} from "@/lib/contact-form";
import { EASE_IN, EASE_OUT, gsap } from "@/lib/motion/gsap";

function FieldError({ message }: { message?: string }) {
  return (
    <p className="form_field_error small label" aria-live="polite">
      {message ?? "\u00a0"}
    </p>
  );
}

type ContactFormProps = {
  /** Scope success animation to this root (subpages). Spine uses global selectors. */
  scoped?: boolean;
  className?: string;
  showHeadline?: boolean;
};

export function ContactForm({
  scoped = false,
  className,
  showHeadline = true,
}: ContactFormProps) {
  const site = useContent();
  const rootRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [budget, setBudget] = useState("");
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryIso>(
    defaultPhoneCountryIso(),
  );
  const [currency, setCurrency] = useState<ContactCurrency>(
    currencyForCountry(defaultPhoneCountryIso()),
  );

  const mutation = useMutation({
    mutationFn: (values: ContactFormValues) =>
      submitContactForm(values, site.locale),
    onSuccess: () => {
      setSubmitError(null);
      const root = scoped ? rootRef.current : document;
      if (!root) return;
      const wrap = root.querySelector(".form_wrap");
      const success = root.querySelector(".form_success");
      if (!wrap || !success) return;
      gsap
        .timeline()
        .to(wrap, { autoAlpha: 0, duration: 0.5, ease: EASE_IN })
        .to(success, { autoAlpha: 1, duration: 0.5, ease: EASE_OUT });
    },
    onError: (err) => {
      const message =
        err instanceof ContactSubmissionError
          ? err.message
          : site.contact.errors.submitFailed;
      setSubmitError(message || site.contact.errors.submitFailed);
    },
  });

  useEffect(() => {
    const detected = detectPhoneCountryIso();
    setPhoneCountry(detected);
    setCurrency(currencyForCountry(detected));
  }, []);

  function onCountryChange(iso: PhoneCountryIso) {
    setPhoneCountry(iso);
    setCurrency(currencyForCountry(iso));
    setErrors((prev) => ({
      ...prev,
      phone: undefined,
      phoneCountry: undefined,
    }));
  }

  function onBudgetChange(e: ChangeEvent<HTMLInputElement>) {
    const el = e.currentTarget;
    const { value, caret } = formatBudgetWithCaret(
      el.value,
      el.selectionStart ?? el.value.length,
    );
    setBudget(value);
    setErrors((prev) => ({ ...prev, budget: undefined }));
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(caret, caret);
      } catch {
        /* ignore */
      }
    });
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const raw = {
      name: String(fd.get("name") ?? ""),
      business: String(fd.get("business") ?? ""),
      phoneCountry: String(fd.get("phoneCountry") ?? phoneCountry),
      phone: String(fd.get("phone") ?? ""),
      email: String(fd.get("email") ?? ""),
      budget: String(fd.get("budget") ?? ""),
      currency: String(fd.get("currency") ?? currency),
      details: String(fd.get("details") ?? ""),
    };
    const parsed = contactFormSchema(site.contact.errors).safeParse(raw);
    if (!parsed.success) {
      setErrors(contactFormErrorsFromZod(parsed.error));
      setSubmitError(null);
      return;
    }
    setErrors({});
    setSubmitError(null);
    mutation.mutate(parsed.data);
  }

  const idPrefix = scoped ? "page-" : "";

  return (
    <div
      ref={rootRef}
      className={scoped ? (className ?? "f f-c") : className}
      style={scoped ? undefined : { display: "contents" }}
      data-contact-form={scoped ? "page" : "spine"}
    >
      <div className="form_success f f-c a-c j-c">
        <h4>{site.contact.successTitle}</h4>
        <span className="small label">{site.contact.successMessage}</span>
      </div>
      <div className="form_wrap f f-c">
        {showHeadline ? <h4 className="_ele">{site.contact.headline}</h4> : null}
        <form
          ref={formRef}
          action="#"
          className="_form"
          method="POST"
          data-client-validate=""
          noValidate
          onSubmit={onSubmit}
        >
          <div className="form_set f f-w">
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}name`}>
                {site.contact.form.nameLabel}
              </label>
              <input
                className="input"
                name="name"
                id={`${idPrefix}name`}
                placeholder={site.contact.form.namePlaceholder}
                autoComplete="name"
                aria-invalid={Boolean(errors.name)}
                onChange={() =>
                  setErrors((prev) => ({ ...prev, name: undefined }))
                }
              />
              <FieldError message={errors.name} />
            </div>
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}business`}>
                {site.contact.form.businessLabel}{" "}
                <span className="light_60">{site.contact.form.businessOptional}</span>
              </label>
              <input
                className="input"
                name="business"
                id={`${idPrefix}business`}
                placeholder={site.contact.form.businessPlaceholder}
                autoComplete="organization"
                aria-invalid={Boolean(errors.business)}
                onChange={() =>
                  setErrors((prev) => ({ ...prev, business: undefined }))
                }
              />
              <FieldError message={errors.business} />
            </div>
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}phone`}>
                {site.contact.form.phoneLabel}
              </label>
              <div className="budget_set phone_set f a-c">
                <CountryCodeSelect
                  value={phoneCountry}
                  onCountryChange={onCountryChange}
                />
                <input
                  className="input"
                  type="tel"
                  name="phone"
                  id={`${idPrefix}phone`}
                  placeholder={site.contact.form.phonePlaceholder}
                  dir="ltr"
                  autoComplete="tel-national"
                  aria-invalid={Boolean(errors.phone)}
                  onChange={() =>
                    setErrors((prev) => ({ ...prev, phone: undefined }))
                  }
                />
              </div>
              <FieldError message={errors.phone ?? errors.phoneCountry} />
            </div>
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}email`}>
                {site.contact.form.emailLabel}{" "}
                <span className="light_60">{site.contact.form.emailOptional}</span>
              </label>
              <input
                className="input"
                type="email"
                name="email"
                id={`${idPrefix}email`}
                placeholder={site.contact.form.emailPlaceholder}
                dir="ltr"
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                onChange={() =>
                  setErrors((prev) => ({ ...prev, email: undefined }))
                }
              />
              <FieldError message={errors.email} />
            </div>
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}budget`}>
                {site.contact.form.budgetLabel}
              </label>
              <div className="budget_set f a-c">
                <CurrencySelect
                  value={currency}
                  onCurrencyChange={(next) => {
                    setCurrency(next);
                    setErrors((prev) => ({ ...prev, currency: undefined }));
                  }}
                />
                <input
                  className="input"
                  type="text"
                  name="budget"
                  id={`${idPrefix}budget`}
                  dir="ltr"
                  autoComplete="off"
                  placeholder={site.contact.form.budgetPlaceholder}
                  value={budget}
                  onChange={onBudgetChange}
                  aria-invalid={Boolean(errors.budget)}
                />
              </div>
              <FieldError message={errors.budget} />
            </div>
            <div className="form_row f f-c _ele">
              <label className="small label" htmlFor={`${idPrefix}details`}>
                {site.contact.form.detailsLabel}
              </label>
              <textarea
                name="details"
                id={`${idPrefix}details`}
                placeholder={site.contact.form.detailsPlaceholder}
                aria-invalid={Boolean(errors.details)}
                onChange={() =>
                  setErrors((prev) => ({ ...prev, details: undefined }))
                }
              />
              <FieldError message={errors.details} />
            </div>
            {submitError ? (
              <div className="form_row f f-c _ele" role="alert">
                <p className="form_field_error small label">{submitError}</p>
              </div>
            ) : null}
            <div className="form_row f a-s _ele">
              <input type="submit" name="submit" className="_submit" />
              <PillButton
                label={site.contact.submit}
                onClick={() => formRef.current?.requestSubmit()}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
